import { CallTranscriptMessageSource, CallTranscriptMessageType } from '../realtime/contracts';

export { CallTranscriptMessageSource, CallTranscriptMessageType };
export type { CallTranscriptMessageSource as CallTranscriptMessageSourceName } from '../realtime/contracts';

export type CallTranscriptWordTimestamp = {
  word: string;
  start: number;
  end: number;
};

export type CallTranscriptMessage = {
  startOffset: number;
  endOffset: number;
  source: CallTranscriptMessageSource;
  originalContent: string;
  content: string;
  type: CallTranscriptMessageType;
  recognizedWords?: CallTranscriptWordTimestamp[] | null;
  messageId?: string | null;
  parentMessageId?: string | null;
};

export type CallTranscriptEventCategory = number;

export type CallTranscriptEvent = {
  offset: number;
  category: CallTranscriptEventCategory;
  title: string;
  description: string;
};

export type CallTranscript = {
  messages: CallTranscriptMessage[];
  detectedEvents: CallTranscriptEvent[];
  summary?: string | null;
};

const SOURCE_ORDER: Record<CallTranscriptMessageSource, number> = {
  [CallTranscriptMessageSource.Caller]: 0,
  [CallTranscriptMessageSource.Callee]: 1,
};

function compareMessages(a: CallTranscriptMessage, b: CallTranscriptMessage): number {
  return (
    a.startOffset - b.startOffset ||
    a.endOffset - b.endOffset ||
    SOURCE_ORDER[a.source] - SOURCE_ORDER[b.source]
  );
}

export function mergeMessage(transcript: CallTranscript, message: CallTranscriptMessage): CallTranscript {
  if (!transcript) {
    throw new Error('transcript must not be null');
  }
  if (!message) {
    throw new Error('message must not be null');
  }

  const messages = [...transcript.messages].sort(compareMessages);

  const existingIndex = findExistingMessageIndex(messages, message);

  if (existingIndex >= 0) {
    const updatedMessage = applyTranscriptMessageUpdate(messages[existingIndex] as CallTranscriptMessage, message);
    if (updatedMessage === null) {
      return transcript;
    }

    messages[existingIndex] = updatedMessage;
  } else {
    const insertedMessage = applyTranscriptMessageUpdate(null, message);
    if (insertedMessage === null) {
      return transcript;
    }

    const insertIndex = messages.findIndex(
      (existingMessage) =>
        existingMessage.startOffset > insertedMessage.startOffset ||
        (existingMessage.startOffset === insertedMessage.startOffset &&
          (existingMessage.endOffset > insertedMessage.endOffset ||
            (existingMessage.endOffset === insertedMessage.endOffset &&
              existingMessage.source > insertedMessage.source))),
    );

    if (insertIndex < 0) {
      messages.push(insertedMessage);
    } else {
      messages.splice(insertIndex, 0, insertedMessage);
    }
  }

  return { messages, detectedEvents: transcript.detectedEvents, summary: transcript.summary };
}

export function orderMessages(messages: Iterable<CallTranscriptMessage>): CallTranscriptMessage[] {
  return [...messages].sort(compareMessages);
}

export function findExistingMessageIndex(
  messages: readonly CallTranscriptMessage[],
  message: CallTranscriptMessage,
): number {
  if (message.messageId != null && message.messageId.trim() !== '') {
    const existingByIdIndex = messages.findIndex((existingMessage) => existingMessage.messageId === message.messageId);

    if (existingByIdIndex >= 0) {
      return existingByIdIndex;
    }
  }

  const existingIndex = messages.findIndex(
    (existingMessage) => existingMessage.source === message.source && existingMessage.startOffset === message.startOffset,
  );

  if (existingIndex >= 0) {
    return existingIndex;
  }

  return messages.findIndex(
    (existingMessage) =>
      existingMessage.source === message.source &&
      existingMessage.type === CallTranscriptMessageType.InterimTranscript &&
      existingMessage.startOffset <= message.endOffset &&
      message.startOffset <= existingMessage.endOffset,
  );
}

export function applyTranscriptMessageUpdate(
  existingMessage: CallTranscriptMessage | null,
  incomingMessage: CallTranscriptMessage,
): CallTranscriptMessage | null {
  const incomingType = incomingMessage.type;

  if (existingMessage === null) {
    if (incomingType === CallTranscriptMessageType.EnhancedTranscript) {
      return null;
    }

    switch (incomingType) {
      case CallTranscriptMessageType.InterimTranscript:
        return createInterimMessage(incomingMessage);
      case CallTranscriptMessageType.FinalTranscript:
        return createFinalMessage(incomingMessage);
      default:
        return null;
    }
  }

  switch (incomingType) {
    case CallTranscriptMessageType.InterimTranscript:
      return applyInterimTranscript(existingMessage, incomingMessage);
    case CallTranscriptMessageType.FinalTranscript:
      return applyFinalTranscript(existingMessage, incomingMessage);
    case CallTranscriptMessageType.EnhancedTranscript:
      return applyEnhancedTranscript(existingMessage, incomingMessage);
    default:
      throw new RangeError(`Unknown incoming message type: ${String(incomingType)}`);
  }
}

function applyInterimTranscript(
  existingMessage: CallTranscriptMessage,
  incomingMessage: CallTranscriptMessage,
): CallTranscriptMessage | null {
  if (existingMessage.type !== CallTranscriptMessageType.InterimTranscript) {
    return null;
  }

  return {
    ...existingMessage,
    startOffset: incomingMessage.startOffset,
    endOffset: incomingMessage.endOffset,
    originalContent: incomingMessage.originalContent,
    content: incomingMessage.content,
    type: CallTranscriptMessageType.InterimTranscript,
    recognizedWords: incomingMessage.recognizedWords,
    messageId: incomingMessage.messageId ?? existingMessage.messageId,
    parentMessageId: incomingMessage.parentMessageId ?? existingMessage.parentMessageId,
  };
}

function applyFinalTranscript(
  existingMessage: CallTranscriptMessage,
  incomingMessage: CallTranscriptMessage,
): CallTranscriptMessage {
  return {
    ...existingMessage,
    startOffset: incomingMessage.startOffset,
    endOffset: incomingMessage.endOffset,
    originalContent: incomingMessage.originalContent,
    content: incomingMessage.content,
    type: CallTranscriptMessageType.FinalTranscript,
    recognizedWords: incomingMessage.recognizedWords,
    messageId: incomingMessage.messageId ?? existingMessage.messageId,
    parentMessageId: incomingMessage.parentMessageId ?? existingMessage.parentMessageId,
  };
}

function applyEnhancedTranscript(
  existingMessage: CallTranscriptMessage,
  incomingMessage: CallTranscriptMessage,
): CallTranscriptMessage | null {
  if (
    existingMessage.type !== CallTranscriptMessageType.FinalTranscript &&
    existingMessage.type !== CallTranscriptMessageType.EnhancedTranscript
  ) {
    return null;
  }

  return {
    ...existingMessage,
    startOffset: incomingMessage.startOffset,
    endOffset: incomingMessage.endOffset,
    content: incomingMessage.content,
    type: CallTranscriptMessageType.EnhancedTranscript,
    recognizedWords: incomingMessage.recognizedWords ?? existingMessage.recognizedWords,
    messageId: incomingMessage.messageId ?? existingMessage.messageId,
    parentMessageId: incomingMessage.parentMessageId ?? existingMessage.parentMessageId,
  };
}

function createInterimMessage(message: CallTranscriptMessage): CallTranscriptMessage {
  return {
    ...message,
    content: message.content,
    type: CallTranscriptMessageType.InterimTranscript,
  };
}

function createFinalMessage(message: CallTranscriptMessage): CallTranscriptMessage {
  return {
    ...message,
    content: message.content,
    type: CallTranscriptMessageType.FinalTranscript,
  };
}
